/**
 * Exposed functions
 */
export const fetchWrapper = {
    getSearchParams,
    generic,
    get,
    post,
    put,
    delete: _delete,
    form,
    multiPartPost,
    multiPartPut,
    getFile
}

function getSearchParams(search) {
    let searchParamsString = ""
    if (search) {
        const searchParams = new URLSearchParams(Object.entries(search).filter(([k, v]) => v))
        // if (searchParams.size > 0) // Use the following alternative because size is not implemented on Ipad
        if ([...new Set(searchParams.keys())].length)
            searchParamsString += '?' + searchParams
    }
    return searchParamsString
}

function generic(url, requestOptions) {
    return fetch(url, requestOptions).then(handleResponse);
}

/**
 * Send an application/json GET request with no parameters.
 *
 * @param url of the web server
 * @param jwt JWT bearer authorization token
 * @param abort abort signal
 * @returns {Promise<Response>}
 */
function get(url, jwt = null, abort = null) {
    const requestOptions = {
        method: 'GET',
        headers: {...authHeader(jwt)},
        signal: abort
    };
    return fetch(url, requestOptions).then(handleResponse);
}

/**
 * Send an application/json POST request with JSON payload.
 *
 * @param url
 * @param payload
 * @param jwt JWT bearer authorization token
 * @returns {Promise<Response>}
 */
function post(url, payload, jwt = null) {
    const requestOptions = {
        method: 'POST',
        headers: {'Content-Type': 'application/json', ...authHeader(jwt)},
        body: JSON.stringify(payload)
    };
    return fetch(url, requestOptions).then(handleResponse).catch(handleError);
}

/**
 * Send an application/json PUT request with JSON payload.
 *
 * @param url
 * @param payload
 * @param jwt JWT bearer authorization token
 * @returns {Promise<Response>}
 */
function put(url, payload, jwt = null) {
    const requestOptions = {
        method: 'PUT',
        headers: {'Content-Type': 'application/json', ...authHeader(jwt)},
        body: JSON.stringify(payload)
    };
    return fetch(url, requestOptions).then(handleResponse).catch(handleError);
}


/**
 * Send an application/json DELETE request with JSON payload.
 *
 * @param url
 * @param jwt JWT bearer authorization token
 * @returns {Promise<Response>}
 */
function _delete(url, jwt = null) {
    const requestOptions = {
        method: 'DELETE',
        headers: {...authHeader(jwt)}
    };
    return fetch(url, requestOptions).then(handleResponse).catch(handleError);
}

/**
 * Upload a media file
 * @param url
 * @param formData
 * @param jwt
 * @returns {Promise<Response>}
 */
function multiPartPost(url, formData, jwt = null) {
    const requestOptions = {
        method: 'POST',
        headers: {...authHeader(jwt)},  // Do not set "Content-Type": "multipart/form-data" it will be set internally
        body: formData
    }
    return fetch(url, requestOptions).then(handleResponse).catch(handleError)
}

/**
 * Upload a media file
 * @param url
 * @param formData
 * @param jwt
 * @returns {Promise<Response>}
 */
function multiPartPut(url, formData, jwt = null) {
    const requestOptions = {
        method: 'PUT',
        headers: {...authHeader(jwt)},  // Do not set "Content-Type": "multipart/form-data" it will be set internally
        body: formData
    }
    return fetch(url, requestOptions).then(handleResponse).catch(handleError)
}

/**
 * Send a standard x-www-form-urlencoded POST request.
 *
 * @param url of the web server
 * @param params key-value parameters
 * @returns {Promise<Response>}
 */
function form(url, params) {
    // Prepare URL encoded parameters
    const searchParams = new URLSearchParams();
    for (const prop in params) {
        searchParams.set(prop, params[prop]);
    }

    // Make a POST request without specifying the application type that instead will set by the body.
    const requestOptions = {
        method: 'POST',
        // headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        // credentials: 'include',
        body: searchParams
    };

    // Finally make the AJAX request
    return fetch(url, requestOptions).then(handleResponse).catch(handleError);
}

function getFile(url, jwt = null, abort = null) {
    const requestOptions = {
        method: 'GET',
        headers: {...authHeader(jwt)},
        signal: abort
    };
    return fetch(url, requestOptions);
}

/** PRIVATE FUNCTIONS **/

/**
 * Append authorization header when JWT is available (means user logged in).
 *
 * @returns {{Authorization: string}|{}}
 */
function authHeader(jwt) {
    if (jwt) {
        return {Authorization: `Bearer ${jwt}`};
    } else {
        return null;
    }
}


/**
 * Pre-fetch server response and always return a JSON object.
 *
 * @param response is the HTTP response
 * @returns a JSON object
 */
function handleResponse(response) {
    return response.text()
        .then(text => {
            if (response.ok) {
                try {
                    // return {"error": null, "payload": JSON.parse(text)};
                    return JSON.parse(text);
                } catch {
                    return Promise.reject({success: false, "message": text, "error": "Cannot JSON parse server's answer."}); // Promise.reject will call .catch
                }
            } else {
                if ([401, 403].includes(response.status)) {
                    // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
                    //sessionService.getBrowserSession().onLogout();
                    console.warn("Unauthorized", response.status)
                    return Promise.reject({success: false, "message": text, "error": response.status});
                } else if ([404].includes(response.status)) {
                    console.warn("404 Page not found");
                    return Promise.reject({success: false, "message": text, "error": "Page not found: " + response.status});
                } else if ([422].includes(response.status)) {
                    const json = JSON.parse(text)
                    console.warn(json.details);
                    return Promise.reject({success: false, "message": text, "error": json.message});
                } else if ([500].includes(response.status)) {
                    console.warn("Internal server error");
                    return Promise.reject({success: false, "message": text, "error": "Internal server error: " + response.status});
                }
                // Extract the error message and send back to the caller
                // const data = text && JSON.parse(text);
                // const error = (data && data.msg) || response.statusText;
                // console.error("HTTP", "Unauthorized: " + response.status, "msg:", error);
                return Promise.reject({error: `Server error: ${response.status}`, message: text, status: response.status});
            }
        })
    // .catch(error => {
    //     console.log(error);
    //     return Promise.reject({"error": "Call to fetch failed with an error", "payload": error});
    // });
}

function handleError(error) {
    if (error !== null && typeof error === 'object') {
        // If error is an object, it comes from Promise.reject in "handleResponse" .then
        return Promise.reject(error);
    }
    console.error("HandleError routine", error);
    return Promise.reject({"error": "Fetch raised an exception.", "message": error});
}